import { ChangeDetectionStrategy, Component, input, signal, computed, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { QuickActionItem } from './quick-action.model';
import { AddIconComponent , ChevronRightIconComponent } from '@kuiper/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { ClickOutSideDirective } from '../../directives/clickOutSide.directive';
import { ModalServiceService } from '../modal/modal-service.service';

@Component({
  selector: 'lib-quick-action',
  imports: [
    NgTemplateOutlet,
    AddIconComponent,
    TranslocoPipe,
    ChevronRightIconComponent,
    ClickOutSideDirective,
  ],
  templateUrl: './quick-action.component.html',
  styleUrl: './quick-action.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class QuickActionComponent {
  private readonly modalService = inject(ModalServiceService);
  quickActionItems = input.required<QuickActionItem[]>();
  isSideMenuCollapsed = input<boolean>(false);
  createRequestSettingModalOpen = input<boolean>(false);
  isQuickActionOpen = signal<boolean>(false);
  private readonly isQuickActionSubOpenOverride = signal<boolean[] | null>(
    null
  );

  // Computed signal for the reset state (all false) with correct length
  private readonly resetState = computed(() =>
    this.quickActionItems().map(() => false)
  );

  // Computed signal that uses override if set, otherwise uses reset state
  isQuickActionSubOpen = computed(() => {
    const override = this.isQuickActionSubOpenOverride();
    return override ?? this.resetState();
  });

  // Computed signal for items with their open state for better performance
  quickActionItemsWithState = computed(() => {
    const items = this.quickActionItems();
    const openStates = this.isQuickActionSubOpen();
    return items.map((item, index) => ({
      ...item,
      isChildOpen: openStates[index] || false,
    }));
  });

  toggleQuickAction() {
    this.isQuickActionOpen.set(!this.isQuickActionOpen());
    if (!this.isQuickActionOpen()) {
      this.onQuickActionItemClick(null);
    }
  }

  onQuickActionItemClick(index: number | null) {
    const currentState = this.isQuickActionSubOpen();
    const newState = currentState.map((_, i) => i === index);
    this.isQuickActionSubOpenOverride.set(newState);
  }

  onClickOutside() {
    // Check the actual modal state from the modal service instead of relying on mutated properties
    if (this.modalService.modalContent().isOpen) return;
    this.isQuickActionOpen.set(false);
    this.isQuickActionSubOpenOverride.set(null);
  }
}
